<div class="app-dialog">
  <header class="app-dialog__header">
    <div class="app-dialog__header-text">
      <h2>{{ 'editClient.header' | translate }}</h2>
    </div>
    <button mat-icon-button type="button" (click)="close()" [attr.aria-label]="'cancel' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="app-dialog__body">
    <form [formGroup]="form" (submit)="submit()" class="form">
      <div class="form-field">
        <label class="field-label">{{ 'client.domain' | translate }}</label>
        <mat-form-field appearance="outline">
          <input type="text" matInput formControlName="domain" [errorStateMatcher]="errorStateMatcher">
        </mat-form-field>
        <mat-checkbox
          *ngIf="isDomainChanged"
          formControlName="domainChangeAcknowledged"
          class="change-domain-checkbox"
          color="primary"
        >
          {{'editClient.changeDomainWarning' | translate}}
        </mat-checkbox>
      </div>

      <div class="form-field">
        <label class="field-label">{{ 'client.googleAdminEmail' | translate }}</label>
        <mat-form-field appearance="outline">
          <input type="text" matInput formControlName="googleAdminEmail" [errorStateMatcher]="errorStateMatcher">
        </mat-form-field>
      </div>

      <div class="form-field">
        <label class="field-label">{{ 'editClient.subscription' | translate }}</label>
        <mat-form-field appearance="outline">
          <mat-select formControlName="planId" [errorStateMatcher]="errorStateMatcher">
            <mat-option *ngFor="let subscription of subscriptions" [value]="subscription">{{subscription}}</mat-option>
          </mat-select>
          <mat-error *ngIf="form.hasError('required', ['planId'])">{{'fieldRequired' | translate}}</mat-error>
        </mat-form-field>
      </div>

      <div class="form-field">
        <label class="field-label">{{ 'client.language' | translate }}</label>
        <mat-form-field appearance="outline">
          <mat-select formControlName="language" [errorStateMatcher]="errorStateMatcher">
            <mat-option value="en">EN</mat-option>
            <mat-option value="es">ES</mat-option>
            <mat-option value="pl">PL</mat-option>
          </mat-select>
          <mat-error *ngIf="form.hasError('required', ['language'])">{{'fieldRequired' | translate}}</mat-error>
        </mat-form-field>
      </div>

      <div class="form-field" *ngIf="form.get('planId').value === 'free'">
        <label class="field-label">{{ 'client.trialFinishDate' | translate }}</label>
        <mat-form-field appearance="outline">
          <input matInput [matDatepicker]="picker" formControlName="trialFinishDate" [errorStateMatcher]="errorStateMatcher">
          <mat-hint>YYYY-MM-DD</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
          <mat-error *ngIf="form.hasError('required', ['trialFinishDate'])">{{'fieldRequired' | translate}}</mat-error>
          <mat-error *ngIf="form.hasError('dateInvalid', ['trialFinishDate'])">{{'futureDateInvalid' | translate}}</mat-error>
        </mat-form-field>
      </div>

      <div class="form-field">
        <label class="field-label">{{ 'editClient.billingType' | translate }}</label>
        <mat-form-field appearance="outline">
          <mat-select formControlName="billingMode" [errorStateMatcher]="errorStateMatcher">
            <mat-option *ngFor="let mode of billingModes" [value]="mode">
              {{ ('client.billingType.' + mode) | translate }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field">
        <mat-checkbox formControlName="excludedNewEmployees" color="primary">
          {{'editClient.excludedNewEmployees' | translate}}
        </mat-checkbox>
      </div>

      <section class="optional-interfaces" formGroupName="optionalInterfaces">
        <h3 class="optional-interfaces__title">{{ 'editClient.optionalInterfaces.title' | translate }}</h3>
        <p class="optional-interfaces__hint">{{ 'editClient.optionalInterfaces.hint' | translate }}</p>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.appsTitle' | translate }}</label>
          <mat-checkbox formControlName="mobileApps" color="primary">
            {{ 'editClient.optionalInterfaces.mobileApps' | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="browserExtension" color="primary">
            {{ 'editClient.optionalInterfaces.browserExtension' | translate }}
          </mat-checkbox>
        </div>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.walletTitle' | translate }}</label>
          <mat-checkbox formControlName="appleWallet" color="primary">
            {{ 'editClient.optionalInterfaces.appleWallet' | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="googleWallet" color="primary">
            {{ 'editClient.optionalInterfaces.googleWallet' | translate }}
          </mat-checkbox>
        </div>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.brandingTitle' | translate }}</label>
          <mat-checkbox formControlName="emailSignatures" color="primary">
            {{ 'editClient.optionalInterfaces.emailSignatures' | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="virtualBackgrounds" color="primary">
            {{ 'editClient.optionalInterfaces.virtualBackgrounds' | translate }}
          </mat-checkbox>
        </div>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.linkPreviewTitle' | translate }}</label>
          <mat-radio-group formControlName="linkPreviewMode" class="optional-interfaces__radios">
            <mat-radio-button *ngFor="let mode of linkPreviewModes" [value]="mode" color="primary">
              {{ ('editClient.optionalInterfaces.linkPreview.' + mode) | translate }}
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.digitalCardTitle' | translate }}</label>
          <mat-checkbox formControlName="digitalCard" color="primary">
            {{ 'editClient.optionalInterfaces.digitalCard' | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="selfPrintCards" color="primary">
            {{ 'editClient.optionalInterfaces.selfPrintCards' | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="contextualQr" color="primary">
            {{ 'editClient.optionalInterfaces.contextualQr' | translate }}
          </mat-checkbox>
        </div>

        <div class="optional-interfaces__group">
          <label class="field-label">{{ 'editClient.optionalInterfaces.namePronunciationTitle' | translate }}</label>
          <mat-checkbox formControlName="namePronunciation" color="primary">
            {{ 'editClient.optionalInterfaces.namePronunciation' | translate }}
          </mat-checkbox>
          <ng-container *ngIf="form.get('optionalInterfaces.namePronunciation')?.value">
            <mat-radio-group formControlName="namePronunciationScope" class="optional-interfaces__radios">
              <mat-radio-button *ngFor="let scope of namePronunciationScopes" [value]="scope" color="primary">
                {{ ('editClient.optionalInterfaces.namePronunciationScope.' + scope) | translate }}
              </mat-radio-button>
            </mat-radio-group>
            <mat-form-field
              appearance="outline"
              *ngIf="form.get('optionalInterfaces.namePronunciationScope')?.value === 'designated_roles'"
              class="optional-interfaces__roles">
              <mat-label>{{ 'editClient.optionalInterfaces.namePronunciationRoles' | translate }}</mat-label>
              <mat-select formControlName="namePronunciationRoles" multiple [errorStateMatcher]="errorStateMatcher">
                <mat-option *ngFor="let role of namePronunciationRoleOptions" [value]="role">
                  {{ role }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </ng-container>
        </div>
      </section>
    </form>
  </div>

  <footer class="app-dialog__footer">
    <button mat-stroked-button type="button" (click)="close()">{{'cancel' | translate}}</button>
    <button
      mat-raised-button
      color="primary"
      type="button"
      (click)="submit()"
      [disabled]="sendingRequest || (isDomainChanged && !form.get('domainChangeAcknowledged')?.value)"
    >
      <mat-icon *ngIf="!sendingRequest">save</mat-icon>
      <mat-spinner diameter="20" class="dc-btn-spinner" *ngIf="sendingRequest"></mat-spinner>
      <span *ngIf="!sendingRequest">{{'save' | translate}}</span>
    </button>
  </footer>
</div>
